import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../hooks/useAuth';

// ─── Types ──────────────────────────────────────────────────

interface FunnelStep {
  step: string;
  eventCount: number;
  users: number;
}

interface FunnelBySource {
  source: string;
  steps: FunnelStep[];
}

interface FunnelData {
  dateRange: { startDate: string; endDate: string };
  funnel: FunnelStep[];
  funnelBySource: FunnelBySource[];
}

interface WaitlistAnalytics {
  dateRange: { startDate: string; endDate: string };
  joined: number;
  converted: number;
  waiting: number;
  expired: number;
  conversionRate: number;
  avgWaitMinutes: number | null;
}

// ─── Constants ──────────────────────────────────────────────

const STEP_LABELS: Record<string, string> = {
  page_view: 'Page View',
  book_now_click: 'Book Now Click',
  begin_checkout: 'Begin Checkout',
  purchase: 'Purchase',
};

const STEP_COLORS = ['bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-green-500'];

const DATE_RANGES = [
  { label: '7 Days', startDate: '7daysAgo', endDate: 'today', days: 7 },
  { label: '30 Days', startDate: '30daysAgo', endDate: 'today', days: 30 },
  { label: '90 Days', startDate: '90daysAgo', endDate: 'today', days: 90 },
];

function isoDateNDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function isoDateToday(): string {
  return new Date().toISOString().slice(0, 10);
}

// ─── Component ──────────────────────────────────────────────

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, getToken } = useAuth();

  const [funnelData, setFunnelData] = useState<FunnelData | null>(null);
  const [waitlistData, setWaitlistData] = useState<WaitlistAnalytics | null>(null);
  const [waitlistError, setWaitlistError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRange, setSelectedRange] = useState(1); // Default: 30 days

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchFunnelData = async () => {
      if (!isAuthenticated) return;

      try {
        setLoading(true);
        setError(null);
        const token = await getToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
        const range = DATE_RANGES[selectedRange];

        const response = await fetch(
          `${apiUrl}/admin/analytics/funnel?startDate=${range.startDate}&endDate=${range.endDate}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) {
          const result = await response.json().catch(() => null);
          throw new Error(result?.error?.message || 'Failed to fetch analytics');
        }

        const result = await response.json();
        if (result.success) {
          setFunnelData(result.data);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchFunnelData();
  }, [isAuthenticated, getToken, selectedRange]);

  useEffect(() => {
    const fetchWaitlistData = async () => {
      if (!isAuthenticated) return;
      try {
        setWaitlistError(null);
        const token = await getToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
        const range = DATE_RANGES[selectedRange];
        const startDate = isoDateNDaysAgo(range.days);
        const endDate = isoDateToday();

        const response = await fetch(
          `${apiUrl}/admin/analytics/waitlist?startDate=${startDate}&endDate=${endDate}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) {
          const result = await response.json().catch(() => null);
          throw new Error(result?.error?.message || 'Failed to fetch waitlist analytics');
        }
        const result = await response.json();
        if (result.success) setWaitlistData(result.data);
      } catch (err) {
        setWaitlistError(err instanceof Error ? err.message : 'Failed to load waitlist analytics');
      }
    };

    fetchWaitlistData();
  }, [isAuthenticated, getToken, selectedRange]);

  if (authLoading || loading) {
    return (
      <AdminLayout title="Analytics">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Analytics">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-800 font-medium mb-2">Analytics Unavailable</p>
          <p className="text-sm text-yellow-700">{error}</p>
        </div>
      </AdminLayout>
    );
  }

  const funnel = funnelData?.funnel || [];
  const funnelBySource = funnelData?.funnelBySource || [];
  const maxEventCount = Math.max(...funnel.map((s) => s.eventCount), 1);

  // Overall conversion rate: purchase events / unique visitors (page_view users)
  const pageViewUsers = funnel[0]?.users || 0;
  const purchases = funnel[funnel.length - 1]?.eventCount || 0;
  const overallRate = pageViewUsers > 0 ? ((purchases / pageViewUsers) * 100).toFixed(2) : '0.00';

  return (
    <AdminLayout
      title="Analytics"
      headerRight={
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {DATE_RANGES.map((range, idx) => (
            <button
              key={range.label}
              onClick={() => setSelectedRange(idx)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                selectedRange === idx
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      }
    >
      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-5">
          <p className="text-sm text-gray-500">Visitors</p>
          <p className="text-2xl font-bold text-gray-900">{pageViewUsers.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-5">
          <p className="text-sm text-gray-500">Purchases</p>
          <p className="text-2xl font-bold text-gray-900">{purchases.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-5">
          <p className="text-sm text-gray-500">Overall Conversion</p>
          <p className="text-2xl font-bold text-gray-900">{overallRate}%</p>
          <p className="text-xs text-gray-400 mt-1">Purchases / Visitors</p>
        </div>
      </div>

      {/* Conversion Funnel */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Conversion Funnel</h2>
        <div className="space-y-4">
          {funnel.map((step, idx) => {
            const prevCount = idx > 0 ? funnel[idx - 1].eventCount : step.eventCount;
            const stepRate = prevCount > 0 ? ((step.eventCount / prevCount) * 100).toFixed(1) : '—';
            const barWidth = (step.eventCount / maxEventCount) * 100;

            return (
              <div key={step.step}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-900 w-36">
                      {STEP_LABELS[step.step] || step.step}
                    </span>
                    <span className="text-sm text-gray-500">
                      {step.eventCount.toLocaleString()} events
                    </span>
                    <span className="text-sm text-gray-400">
                      ({step.users.toLocaleString()} users)
                    </span>
                  </div>
                  {idx > 0 && (
                    <span
                      className={`text-sm font-medium ${
                        parseFloat(stepRate) >= 50
                          ? 'text-green-600'
                          : parseFloat(stepRate) >= 20
                            ? 'text-yellow-600'
                            : 'text-red-600'
                      }`}
                    >
                      {stepRate}%
                    </span>
                  )}
                </div>
                <div className="w-full bg-gray-100 rounded-full h-6">
                  <div
                    className={`h-6 rounded-full ${STEP_COLORS[idx] || 'bg-gray-500'} transition-all duration-500`}
                    style={{ width: `${Math.max(barWidth, 1)}%` }}
                  />
                </div>
                {idx < funnel.length - 1 && (
                  <div className="flex justify-center py-1">
                    <svg
                      className="w-4 h-4 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      />
                    </svg>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {funnel.length === 0 && (
          <p className="text-center text-gray-500 py-8">
            No funnel data available for this period. GA4 data may take 24-48 hours to appear.
          </p>
        )}
      </div>

      {/* Waitlist Conversion */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Waitlist</h2>
        {waitlistError ? (
          <div className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-3">
            {waitlistError}
          </div>
        ) : waitlistData ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-4">
              <MetricCard label="Joined" value={waitlistData.joined.toLocaleString()} />
              <MetricCard label="Converted" value={waitlistData.converted.toLocaleString()} />
              <MetricCard label="Still Waiting" value={waitlistData.waiting.toLocaleString()} />
              <MetricCard label="Expired" value={waitlistData.expired.toLocaleString()} />
              <MetricCard
                label="Conversion Rate"
                value={`${(waitlistData.conversionRate * 100).toFixed(1)}%`}
                emphasis
              />
            </div>
            <p className="text-sm text-gray-500">
              Average wait time:{' '}
              <span className="font-medium text-gray-900">
                {waitlistData.avgWaitMinutes === null
                  ? '—'
                  : formatWaitMinutes(waitlistData.avgWaitMinutes)}
              </span>
              {waitlistData.converted === 0 && (
                <span className="ml-1 text-gray-400">(no conversions in window)</span>
              )}
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-500">Loading…</p>
        )}
      </div>

      {/* Funnel by Source */}
      {funnelBySource.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Funnel by Traffic Source</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Source</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Page Views</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Book Now</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Checkout</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Purchase</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Conv. Rate</th>
                </tr>
              </thead>
              <tbody>
                {funnelBySource.map((row) => {
                  const views = row.steps[0]?.eventCount || 0;
                  const purch = row.steps[3]?.eventCount || 0;
                  const rate = views > 0 ? ((purch / views) * 100).toFixed(2) : '0.00';

                  return (
                    <tr key={row.source} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-2 font-medium text-gray-900">
                        {row.source === '(direct)' ? 'Direct' : row.source}
                      </td>
                      {row.steps.map((step) => (
                        <td key={step.step} className="text-right py-3 px-2 text-gray-700">
                          {step.eventCount.toLocaleString()}
                        </td>
                      ))}
                      <td className="text-right py-3 px-2">
                        <span
                          className={`font-medium ${
                            parseFloat(rate) >= 5
                              ? 'text-green-600'
                              : parseFloat(rate) > 0
                                ? 'text-yellow-600'
                                : 'text-gray-400'
                          }`}
                        >
                          {rate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function MetricCard({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="bg-gray-50 rounded-lg px-3 py-2.5">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-xl font-bold ${emphasis ? 'text-primary' : 'text-gray-900'}`}>{value}</p>
    </div>
  );
}

function formatWaitMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours < 24) return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const hrLeft = hours % 24;
  return hrLeft ? `${days}d ${hrLeft}h` : `${days}d`;
}
